import { Box, Text, Flex, useColorModeValue, Badge } from '@chakra-ui/react';
import type { APIPersonalHomeApplicationForm } from 'types/api.types';

interface HomeReportHeaderProps {
  applicationData?: APIPersonalHomeApplicationForm | null;
}

interface CellProps {
  label: string;
  value: string;
  isLast?: boolean;
}

const HomeReportHeader = ({ applicationData }: HomeReportHeaderProps) => {
  const textColor = useColorModeValue('gray.700', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const headerBgColor = useColorModeValue('white', 'navy.900');

  const Cell = ({ label, value, isLast = false }: CellProps) => (
    <Box
      flex="1"
      py={3}
      px={4}
      borderRightWidth={isLast ? '0' : '1px'}
      borderColor={borderColor}
    >
      <Text fontSize="xs" color={labelColor} fontWeight="semibold" mb={1}>
        {label}
      </Text>
      {value === '(Missing)' ? (
        <Badge colorScheme="red" fontSize="sm" fontWeight="bold">
          Missing
        </Badge>
      ) : (
        <Text fontWeight="medium" color={textColor} fontSize="sm">
          {value}
        </Text>
      )}
    </Box>
  );

  // Helper function to handle missing vs empty values
  const formatValue = (value: string | undefined | null) => {
    if (value === undefined) {
      return '(Missing)';
    }
    if (value === null || value === '') {
      return '-';
    }
    return value;
  };

  const getApplicantNames = () => {
    const applicants = applicationData?.applicants || [];
    if (applicants.length === 0) {
      return null;
    }
    if (applicants.length === 1) {
      return applicants[0]?.name || null;
    }
    const name1 = applicants[0]?.name || '';
    const name2 = applicants[1]?.name || '';
    return [name1, name2].filter(Boolean).join(' / ');
  };

  const getFullAddress = () => {
    const info = applicationData?.applicant_information;
    if (!info) return null;

    const addressParts = [
      info.address,
      info.city,
      info.province_code,
      info.postal_code,
    ].filter(Boolean);

    return addressParts.length > 0 ? addressParts.join(', ') : null;
  };

  // Normalize time value for display (handles legacy 'am'/'pm' and new 'h:mm AM/PM' format)
  const formatTimeForDisplay = (time: string): string => {
    if (!time) return '';

    // Handle legacy 'am'/'pm' values
    if (time.toLowerCase() === 'am') return '12:01 AM';
    if (time.toLowerCase() === 'pm') return '12:01 PM';

    // Return as-is if already in correct format (h:mm AM/PM)
    return time;
  };

  const getEffectiveDateTime = () => {
    const policyPeriod = applicationData?.policy_period;
    if (!policyPeriod?.effective_date) return null;

    const time = formatTimeForDisplay(policyPeriod.effective_date_time || '');
    return time
      ? `${policyPeriod.effective_date} ${time}`
      : policyPeriod.effective_date;
  };

  const getExpiryDateTime = () => {
    const policyPeriod = applicationData?.policy_period;
    if (!policyPeriod?.expiry_date) return null;

    const time = formatTimeForDisplay(policyPeriod.effective_date_time || '');
    return time
      ? `${policyPeriod.expiry_date} ${time}`
      : policyPeriod.expiry_date;
  };

  const formatPremium = () => {
    const premium =
      applicationData?.method_of_payment?.estimated_policy_premium;
    if (!premium) return null;
    return premium.startsWith('$') ? premium : `$${premium}`;
  };

  const headerData = {
    insuranceCompany: formatValue(
      applicationData?.policy_information?.insurance_company,
    ),
    reportDate: new Date().toLocaleDateString('en-CA'),
    brokerName: formatValue(applicationData?.broker_information?.name),
    branchName: formatValue(applicationData?.broker_information?.branch),
    binderNumber: formatValue(
      applicationData?.policy_information?.binder_number,
    ),
    brokerCode: formatValue(applicationData?.broker_information?.broker_code),
    customerCode: formatValue(
      applicationData?.policy_information?.customer_code,
    ),
    premium: formatValue(formatPremium()),
    producerCode: formatValue(
      (applicationData?.broker_information as Record<string, string>)
        ?.producer_code,
    ),
    csrCode: formatValue(applicationData?.policy_information?.broker_csr_code),
    namedInsured: formatValue(getApplicantNames()),
    insuredAddress: formatValue(getFullAddress()),
    effectiveDateTime: formatValue(getEffectiveDateTime()),
    expiryDateTime: formatValue(getExpiryDateTime()),
  };

  return (
    <Box
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      bg={headerBgColor}
      overflow="hidden"
    >
      {/* Row 1: Insurance Company | Report Date */}
      <Flex borderBottomWidth="1px" borderColor={borderColor}>
        <Cell
          label="Insurance Company (Insurer)"
          value={headerData.insuranceCompany}
        />
        <Cell label="Report Date" value={headerData.reportDate} isLast />
      </Flex>

      {/* Row 2: Broker | Branch */}
      <Flex borderBottomWidth="1px" borderColor={borderColor}>
        <Cell label="Broker" value={headerData.brokerName} />
        <Cell label="Branch" value={headerData.branchName} isLast />
      </Flex>

      {/* Row 3: Binder Number | Broker Code | Customer Code */}
      <Flex borderBottomWidth="1px" borderColor={borderColor}>
        <Cell label="Binder Number" value={headerData.binderNumber} />
        <Cell label="Broker Code" value={headerData.brokerCode} />
        <Cell label="Customer Code" value={headerData.customerCode} isLast />
      </Flex>

      {/* Row 4: Premium | Producer Code | CSR Code */}
      <Flex borderBottomWidth="1px" borderColor={borderColor}>
        <Cell label="Premium" value={headerData.premium} />
        <Cell label="Producer Code" value={headerData.producerCode} />
        <Cell label="CSR Code" value={headerData.csrCode} isLast />
      </Flex>

      {/* Row 5: Named Insured & Address (full width) */}
      <Box py={3} px={4} borderBottomWidth="1px" borderColor={borderColor}>
        <Text fontSize="xs" color={labelColor} fontWeight="semibold" mb={1}>
          Named Insured & Address
        </Text>
        {headerData.namedInsured === '(Missing)' ? (
          <Badge colorScheme="red" fontSize="sm" fontWeight="bold">
            Missing
          </Badge>
        ) : (
          <>
            <Text fontWeight="medium" color={textColor} fontSize="sm">
              {headerData.namedInsured}
            </Text>
            {headerData.insuredAddress !== '(Missing)' &&
              headerData.insuredAddress !== '-' && (
                <Text fontSize="sm" color={textColor}>
                  {headerData.insuredAddress}
                </Text>
              )}
          </>
        )}
      </Box>

      {/* Row 6: Effective Date/Time | Expiry Date/Time */}
      <Flex>
        <Cell
          label="Effective Date/Time"
          value={headerData.effectiveDateTime}
        />
        <Cell
          label="Expiry Date/Time"
          value={headerData.expiryDateTime}
          isLast
        />
      </Flex>
    </Box>
  );
};

export default HomeReportHeader;
